import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import { useRange } from '@/lib/useRange';
import { RANGE_LABELS, type RangeKey } from '@/lib/range';
import { StatCard } from '@/components/StatCard';
import { Card, CardLabel } from '@/components/ui/card';
import { TimeSeriesChart } from '@/components/charts/TimeSeriesChart';
import { DonutChart } from '@/components/charts/DonutChart';
import { HorizontalBarChart } from '@/components/charts/HorizontalBarChart';
import { Link, useLocation } from 'react-router-dom';

interface KpiDoc {
  value: number;
  delta?: number;
  deltaLabel?: string;
}
interface KpisResponse {
  range: RangeKey;
  totalUsers: KpiDoc;
  livePosts: KpiDoc;
  activeUsers: KpiDoc;
  pendingModeration: KpiDoc;
}

interface DailyRow {
  date: string;
  newUsers: number;
  newPosts: number;
  engagementEvents: number;
}
interface DailyActivityResponse {
  range: RangeKey;
  series: DailyRow[];
}

interface MixItem {
  name: string;
  value: number;
}
interface MixResponse {
  items: MixItem[];
  range?: RangeKey;
}

interface GeoResponse {
  totalDistinct: number;
  totalProfiles: number;
  items: MixItem[];
}

function api<T>(path: string): () => Promise<T> {
  return () => apiFetch<T>(path);
}

export function Overview(): JSX.Element {
  const [range] = useRange();
  const location = useLocation();

  const kpis = useQuery({
    queryKey: ['metrics', 'overview', 'kpis', range],
    queryFn: api<KpisResponse>(`/api/metrics/overview/kpis?range=${range}`),
  });
  const daily = useQuery({
    queryKey: ['metrics', 'overview', 'daily-activity', range],
    queryFn: api<DailyActivityResponse>(
      `/api/metrics/overview/daily-activity?range=${range}`,
    ),
  });
  const postMix = useQuery({
    queryKey: ['metrics', 'overview', 'post-type-mix'],
    queryFn: api<MixResponse>('/api/metrics/overview/post-type-mix'),
  });
  const notifMix = useQuery({
    queryKey: ['metrics', 'overview', 'notification-mix', range],
    queryFn: api<MixResponse>(
      `/api/metrics/overview/notification-mix?range=${range}`,
    ),
  });
  const countries = useQuery({
    queryKey: ['metrics', 'users', 'topCountries'],
    queryFn: api<GeoResponse>('/api/metrics/users/top-countries'),
  });
  const cities = useQuery({
    queryKey: ['metrics', 'users', 'topCities'],
    queryFn: api<GeoResponse>('/api/metrics/users/top-cities'),
  });

  const errorMsg = (q: { error: unknown }): string | null =>
    q.error ? (q.error as Error).message : null;

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="mb-1 text-2xl font-bold">Overview</h1>
        <p className="text-sm text-fg-muted">
          Realtime snapshot of Malsi. Range: {RANGE_LABELS[range]}. Polls every
          30 seconds.
        </p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Total users"
          value={kpis.data?.totalUsers.value}
          delta={kpis.data?.totalUsers.delta}
          deltaLabel={kpis.data?.totalUsers.deltaLabel}
          loading={kpis.isLoading}
          error={errorMsg(kpis)}
          hint="All-time, includes archived/banned"
        />
        <StatCard
          label="Live posts"
          value={kpis.data?.livePosts.value}
          delta={kpis.data?.livePosts.delta}
          deltaLabel={kpis.data?.livePosts.deltaLabel}
          loading={kpis.isLoading}
          error={errorMsg(kpis)}
          hint="status=LIVE, not hidden"
        />
        <StatCard
          label={`Active users · ${RANGE_LABELS[range]}`}
          value={kpis.data?.activeUsers.value}
          delta={kpis.data?.activeUsers.delta}
          deltaLabel={kpis.data?.activeUsers.deltaLabel}
          loading={kpis.isLoading}
          error={errorMsg(kpis)}
          hint="Engagement proxy — posts/comments/likes/follows. Malsi has no login event log."
        />
        <StatCard
          label="Pending moderation"
          value={kpis.data?.pendingModeration.value}
          loading={kpis.isLoading}
          error={errorMsg(kpis)}
          hint="Queue depth — review_status='pending'"
        />
      </div>

      {/* Daily activity time-series */}
      <Card className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <div>
            <CardLabel>Daily activity</CardLabel>
            <div className="text-lg font-bold mt-1">
              New users · new posts · engagement events
            </div>
          </div>
        </div>
        {daily.isLoading ? (
          <div className="h-[240px] animate-pulse rounded-md bg-surface-divider/30" />
        ) : daily.error ? (
          <div className="text-sm text-state-danger">{errorMsg(daily)}</div>
        ) : (
          <TimeSeriesChart
            data={daily.data?.series ?? []}
            series={[
              { key: 'newUsers', label: 'New users' },
              { key: 'newPosts', label: 'New posts' },
              { key: 'engagementEvents', label: 'Engagement events' },
            ]}
          />
        )}
      </Card>

      {/* Donuts */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <Card className="flex flex-col gap-4">
          <div>
            <CardLabel>Post types</CardLabel>
            <div className="text-lg font-bold mt-1">Distribution (all-time)</div>
          </div>
          {postMix.isLoading ? (
            <div className="h-[240px] animate-pulse rounded-md bg-surface-divider/30" />
          ) : postMix.error ? (
            <div className="text-sm text-state-danger">{errorMsg(postMix)}</div>
          ) : (
            <DonutChart data={postMix.data?.items ?? []} centerLabel="Posts" />
          )}
        </Card>

        <Card className="flex flex-col gap-4">
          <div>
            <CardLabel>Engagement mix</CardLabel>
            <div className="text-lg font-bold mt-1">
              Notifications by type · {RANGE_LABELS[range]}
            </div>
          </div>
          {notifMix.isLoading ? (
            <div className="h-[240px] animate-pulse rounded-md bg-surface-divider/30" />
          ) : notifMix.error ? (
            <div className="text-sm text-state-danger">{errorMsg(notifMix)}</div>
          ) : (
            <DonutChart data={notifMix.data?.items ?? []} centerLabel="Events" />
          )}
        </Card>
      </div>

      {/* Geography */}
      <Card className="flex flex-col gap-4">
        <div className="flex items-end justify-between">
          <div>
            <CardLabel>Geography</CardLabel>
            <div className="text-lg font-bold mt-1">
              Where Malsi users are
            </div>
          </div>
          <Link
            to={{ pathname: '/users', search: location.search }}
            className="text-xs font-medium text-fg-muted hover:text-fg"
          >
            See all on Users →
          </Link>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_1fr] lg:gap-8">
          <div className="flex flex-col gap-4">
            <GeoSubStat
              label="Countries"
              total={countries.data?.totalDistinct}
              coverage={
                countries.data
                  ? `${countries.data.totalProfiles} of ${verifiedTotalLabel(kpis.data?.totalUsers.value)} profiles`
                  : undefined
              }
              top={countries.data?.items[0]}
              loading={countries.isLoading}
              error={errorMsg(countries)}
            />
            <div>
              <div className="text-xs font-bold uppercase tracking-wide text-fg-muted mb-2">
                Top countries
              </div>
              {countries.isLoading ? (
                <div className="h-[200px] animate-pulse rounded-md bg-surface-divider/30" />
              ) : countries.error ? (
                <div className="text-sm text-state-danger">
                  {errorMsg(countries)}
                </div>
              ) : (
                <HorizontalBarChart
                  data={(countries.data?.items ?? []).slice(0, 5)}
                  height={200}
                  yWidth={160}
                />
              )}
            </div>
          </div>

          <div className="flex flex-col gap-4">
            <GeoSubStat
              label="Cities"
              total={cities.data?.totalDistinct}
              coverage={
                cities.data
                  ? `${cities.data.totalProfiles} of ${verifiedTotalLabel(kpis.data?.totalUsers.value)} profiles`
                  : undefined
              }
              top={cities.data?.items[0]}
              loading={cities.isLoading}
              error={errorMsg(cities)}
            />
            <div>
              <div className="text-xs font-bold uppercase tracking-wide text-fg-muted mb-2">
                Top cities
              </div>
              {cities.isLoading ? (
                <div className="h-[200px] animate-pulse rounded-md bg-surface-divider/30" />
              ) : cities.error ? (
                <div className="text-sm text-state-danger">{errorMsg(cities)}</div>
              ) : (
                <HorizontalBarChart
                  data={(cities.data?.items ?? []).slice(0, 5)}
                  height={200}
                  yWidth={160}
                />
              )}
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}

function verifiedTotalLabel(total: number | undefined): string {
  return total === undefined ? '?' : total.toLocaleString();
}

interface GeoSubStatProps {
  label: string;
  total: number | undefined;
  coverage: string | undefined;
  top: MixItem | undefined;
  loading?: boolean;
  error?: string | null;
}

function GeoSubStat({
  label,
  total,
  coverage,
  top,
  loading,
  error,
}: GeoSubStatProps): JSX.Element {
  if (error) {
    return <div className="text-sm text-state-danger">{error}</div>;
  }
  return (
    <div className="flex items-baseline gap-6">
      <div>
        <div className="text-xs font-bold uppercase tracking-wide text-fg-muted">
          {label}
        </div>
        <div className="text-[28px] font-extrabold leading-none tabular-nums mt-1">
          {loading ? (
            <span className="inline-block h-7 w-14 animate-pulse rounded-md bg-surface-divider/40" />
          ) : (
            (total ?? 0).toLocaleString()
          )}
        </div>
        {coverage && <div className="text-xs text-fg-subtle mt-1">{coverage}</div>}
      </div>
      {top && (
        <div>
          <div className="text-xs font-bold uppercase tracking-wide text-fg-muted">
            Top
          </div>
          <div className="text-base font-bold mt-1">{top.name}</div>
          <div className="text-xs text-fg-subtle">
            {top.value.toLocaleString()} users
          </div>
        </div>
      )}
    </div>
  );
}
